import React, { useContext } from 'react';
import { withRouter } from 'react-router';
import ReactRouterPropTypes from 'react-router-prop-types';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Content } from '@motorway/motorway-storybook-cra';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { routing } from 'Utilities';
import { GA_TAGS } from 'Utilities/analytics';
import { usePageTransitionState } from 'Utilities/animation';
import { vehicleType } from 'Utilities/helpers';
import { useFeatureFlags } from 'Utilities/hooks';

import FooterSpacer from '../../../components/misc/FooterSpacer/FooterSpacer';
import VrmCheckerFooter from '../../../components/vrm/VrmCheckerFooter/VrmCheckerFooter';
import { routes } from '../../../routes';
import Texts from '../../../texts.json';
import { CUSTOMER_HUB_VALUATION_ROUTE } from '../customerHub/CustomerHubHelpers';

import FooterLinks from './Components/FooterLinks/FooterLinks';
import FooterReviews from './Components/FooterReviews/FooterReviews';

import styles from './Footer.scss';

const T = new MDText(Texts);

const Footer = ({ history, location }) => {
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const { userState: user } = useContext(UserContext);
  const { showCognitoLogin } = useFeatureFlags();

  const { animate: transitView } = usePageTransitionState();
  const animate = history?.location?.state?.animateFooter;

  const {
    hideFooter, showDisclaimer, showDisclaimerDetail, showTrackerDisclaimer, useUlezVrmChecker = false, ...route
  } = routing.getMatch(location.pathname, routes);

  // hide footer for cognito journey
  const isCognitoJourneyCheck = showCognitoLogin && (
    (location.pathname === '/sign-in')
    || (route.vrmPath && !Number.isInteger(user?.id))
  );

  if (
    hideFooter
    || (location.pathname === `${CUSTOMER_HUB_VALUATION_ROUTE}/${route?.params?.vrm}` && location.search.includes('redirect'))
    || isCognitoJourneyCheck
  ) {
    return null;
  }

  const showVehicleLookup = !route.hideVehicleLookup;
  const showFooterSpacer = !!route.showFooterSpacer;
  let showReviewsCarousel = !route.hideReviews;

  // hide review on valuation page
  if (route.vrmPath && vehicle?.enquiry?.id) {
    showReviewsCarousel = false;
  }

  const displayText = {
    get shouldShowLookupButtonChevron() {
      return true;
    },

    get vehicleLookupButtonText() {
      return T.translate('footerView.vehicleLookup.buttonLabel');
    },

    get vehicleLookupTitle() {
      if (route?.params?.brand || location.pathname === '/' || route?.params?.partner) {
        return T.translate('footerView.vehicleLookup.homeTitle');
      }

      return T.translate('footerView.vehicleLookup.title', {
        suffix: (<span className={styles.noWrap}>{T.translate('footerView.vehicleLookup.titleSuffix')}</span>),
        vehicleType: vehicleType(location.pathname),
      });
    },
  };

  return (
    <footer
      className={cx({
        [styles.hidden]: transitView,
        [styles.animate]: animate,
      })}
    >
      {showFooterSpacer && <FooterSpacer />}
      {showReviewsCarousel
        ? <FooterReviews />
        : null}
      {showVehicleLookup
        ? (
          <Content
            center
            className={styles.vehicleLookup}
            element="section"
            wrapper={{
              className: styles.vehicleLookupWrapper,
            }}
          >
            {!useUlezVrmChecker && (
              <h4>
                <span data-nosnippet data-cy="footerVrmTitle">
                  {displayText.vehicleLookupTitle}
                </span>
              </h4>
            )}
            <VrmCheckerFooter
              {...{ useUlezVrmChecker }}
              analyticsEvents={{ onInputFocus: GA_TAGS.FOOTER_VRM_INPUT_FOCUSED }}
              buttonProps={{
                className: ' mw-button-large',
                label: displayText.vehicleLookupButtonText,
              }}
              chevron={displayText.shouldShowLookupButtonChevron}
              e2eName="footer vrm checker"
              id="footer-vrm"
            />
          </Content>
        )
        : null}
      <FooterLinks element="section" {...{ showDisclaimer, showDisclaimerDetail, showTrackerDisclaimer }} />
    </footer>
  );
};

Footer.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
};

export default withRouter(Footer);
